import { useCallback, useEffect, useRef } from "react";



const EDGE_MAX = 160; // px; band is this or 25% of the viewport, whichever is smaller
const MIN_SPEED = 10; // px per 60fps-frame when just inside the band
const MAX_SPEED = 140; // px per 60fps-frame at the edge after full acceleration
const RAMP_MS = 450; // time held in the band to reach full speed
const ARM_DISTANCE = 32; // px the cursor must travel from grab point before scrolling

const isScrollable = (el) => {
  if (!el || el === document.body || el === document.documentElement) return false;
  const { overflowY } = getComputedStyle(el);
  return /(auto|scroll)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1;
};

/**
 * Candidate scrollers, innermost first, ending with the document itself.
 * At scroll time we use the first one that actually moves, so a wrapper that
 * is technically "scrollable" by a pixel can't swallow the scroll.
 */
const collectScrollTargets = (el) => {
  const targets = [];
  let node = el?.parentElement;
  while (node) {
    if (isScrollable(node)) targets.push(node);
    node = node.parentElement;
  }
  targets.push(document.scrollingElement || document.documentElement);
  return targets;
};

/** Visible top/bottom (viewport coords) of the scroller we expect to move. */
const visibleBounds = (target) => {
  const vh = window.innerHeight;
  if (!target || target === document.scrollingElement || target === document.documentElement) {
    return { top: 0, bottom: vh };
  }
  const rect = target.getBoundingClientRect();
  return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, vh) };
};

const MOVE_EVENTS = {
  mousemove: ["mousemove"],
  // Native DnD: `dragover` fires on whatever is under the cursor (bubbling to
  // document); `drag` fires on the source. Chrome reports coordinates on both,
  // Firefox reports 0/0 on `drag`, which we ignore.
  dragover: ["dragover", "drag"],
};

const freshState = () => ({
  y: null,
  startY: null,
  armed: false,
  bandEnteredAt: null,
  lastTick: null,
  raf: null,
  targets: [],
  active: null, // scroller that last moved
  onMove: null,
  events: [],
  didEventStep: false,
  eventDriven: false,
});

/**
 * @param {{ moveEvent?: "mousemove" | "dragover" }} [options]
 *   Use "dragover" for native HTML5 drag-and-drop (`draggable` elements):
 *   browsers stop firing `mousemove` during a native drag and emit
 *   `dragover` instead. Default "mousemove" suits react-nestable.
 */
const useDragAutoScroll = ({ moveEvent = "mousemove" } = {}) => {
  const rootRef = useRef(null);
  const state = useRef(freshState());

  const stop = useCallback(() => {
    const s = state.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    if (s.onMove) s.events.forEach((name) => document.removeEventListener(name, s.onMove, true));
    state.current = freshState();
  }, []);

  const start = useCallback(() => {
    stop();
    const s = state.current;
    s.targets = collectScrollTargets(rootRef.current);
    s.active = s.targets[0] || null;
    s.events = MOVE_EVENTS[moveEvent] || [moveEvent];
    const applyScroll = (delta) => {
      // Prefer the scroller that moved last time, then try the rest in order.
      const ordered = s.active ? [s.active, ...s.targets.filter((t) => t !== s.active)] : s.targets;
      for (const target of ordered) {
        const before = target.scrollTop;
        target.scrollTop = before + delta;
        if (target.scrollTop !== before) {
          s.active = target;
          return;
        }
      }
      const beforeY = window.pageYOffset || window.scrollY || 0;
      window.scrollBy(0, delta);
      if ((window.pageYOffset || window.scrollY || 0) !== beforeY) {
        s.active = document.scrollingElement || document.documentElement;
      }
    };

    /** One scroll step; `now` is a performance.now()-style timestamp. */
    const step = (now) => {
      const dt = s.lastTick == null ? 16.67 : Math.min(50, now - s.lastTick);
      s.lastTick = now;
      if (s.y == null) return;

      const { top, bottom } = visibleBounds(s.active);
      const edge = Math.min(EDGE_MAX, (bottom - top) * 0.25);

      let direction = 0;
      let depth = 0; // 0..1, how far into the band
      if (s.y < top + edge) {
        direction = -1;
        depth = Math.min(1, (top + edge - s.y) / edge);
      } else if (s.y > bottom - edge) {
        direction = 1;
        depth = Math.min(1, (s.y - (bottom - edge)) / edge);
      }

      if (direction === 0) {
        // Leaving the band arms the scroller for the rest of this drag.
        s.armed = true;
        s.bandEnteredAt = null;
      } else if (s.armed) {
        if (s.bandEnteredAt == null) s.bandEnteredAt = now;
        const held = Math.min(1, (now - s.bandEnteredAt) / RAMP_MS);
        const proximity = MIN_SPEED + (MAX_SPEED - MIN_SPEED) * depth * depth;
        const speed = proximity * (0.35 + 0.65 * held) * (dt / 16.67);
        applyScroll(direction * speed);
      }
    };

    s.eventDriven = moveEvent === "dragover";
    s.onMove = (e) => {
      if (typeof e.clientY !== "number") return;
      // Firefox fires `drag` with 0/0 coordinates; skip those samples.
      if (e.type === "drag" && e.clientX === 0 && e.clientY === 0) return;
      s.y = e.clientY;
      if (s.startY == null) s.startY = e.clientY;
      if (!s.armed && Math.abs(s.y - s.startY) >= ARM_DISTANCE) s.armed = true;
      const now = performance.now();
      // Native DnD often pauses requestAnimationFrame; drive scrolling from
      // dragover/drag so the page still moves while the pointer is held at an edge.
      if (s.eventDriven) {
        step(now);
        s.didEventStep = true;
      } else if (s.lastTick != null && now - s.lastTick > 100) {
        step(now);
      }
    };
    // Capture phase: no handler further down can stop the sample from reaching us.
    s.events.forEach((name) => document.addEventListener(name, s.onMove, true));

    const tick = (now) => {
      if (!s.didEventStep) step(now);
      s.didEventStep = false;
      s.raf = requestAnimationFrame(tick);
    };
    s.raf = requestAnimationFrame(tick);
  }, [stop, moveEvent]);

  // Clean up if the component unmounts mid-drag.
  useEffect(() => stop, [stop]);

  return { rootRef, onDragStart: start, onDragEnd: stop };
};

export default useDragAutoScroll;
